import React, {FC, useCallback, useEffect, useMemo, useState} from "react";
import {KeyboardAvoidingView, Platform, ScrollView, StyleSheet, Text, View} from "react-native";
import Dropdown from "../../components/Dropdown";
import {Button} from "../../components";
import {ButtonType, Frame, IsMobile} from "../../theme";
import {CircumstancesType} from "../../constants";
import {Storage} from "../../utils/Storage";
import {useAppContext} from "../../contexts/AppContext";
import useAuth from "../../contexts/Auth/useAuth";
import {AppTheme, useTheme} from "../../theme/colors";
import CustomTextInput, {CustomTextInputChangeEvent,} from "../../components/CustomTextInput";
import CurvedBgImage from "../../components/CurvedBgImage";
import Checkbox, {CheckboxPressEvent} from "../../components/Checkbox";
import {NavigationProp, useNavigation} from "@react-navigation/native";
import {HomeTabsParams} from "../../navigation/AppStack/HomeTabParams";
import {ProfileStackRoute} from "../../navigation/AppStack/routes";
import {DeleteScreenRoute} from "../../navigation/ProfileStack/routes";
import {mapStoreDataToProfileFields, university_data} from "../../utils/profile.utils";
import {arraysAreEqual} from "../../utils/passport.utils";
import PageContainer from "../../components/PageContainer";
import {defaultProfileFormValues, IProfileForm} from "./model";
import SaveButton from "../../components/SaveButton";
import {addErrorEntry, getErrorMessage, removeErrorByKey} from '../../utils/errorFormHandlers';
import UniversityDropdown from "../../components/UniversityDropdown";
import * as Application from "expo-application";
import {getSession, updateUserAttributes} from "../../utils/cognitoAuth";
import {WEB_MAXWIDTH} from "../../../constants";


const appVersion = Application.nativeApplicationVersion || "1.0.0"; // Fallback for safety


type ErrorItem = {
    key: string; // Field identifier
    message: string; // Error message for the field
};

const ProfileScreen: FC = () => {
    const [profileFormData, setProfileFormData] = useState<IProfileForm>(
      defaultProfileFormValues
    );
    const {user, setPassportData} = useAuth();
    const {profileFormDataContext, setMode, setProfileFormDataContext} =
      useAppContext();
    const [isAlertVisible, setAlertVisibility] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const theme = useTheme();
    const styles = getStyles(theme);
    const navigatino = useNavigation<NavigationProp<HomeTabsParams>>();
    const storedDataKey = "custom:circumstances";
    const [isFormValueChange, setIsFormValueChange] = useState<boolean>(false);
    const [showLoader, setShowLoader] = useState(false); // TODO remove
    const [savedUniversity, setSavedUniversity] = useState<string | null>(null);
    const {
        setErrorItems,
        errorItems,
        authUpdate,
        setAuthUpdate,
        saveStatus,
        setSaveStatus,
        mode,
        data
    } = useAppContext();

    useEffect(() => {
        const loadSavedUniversity = async () => {
            const savedValue = await Storage.getValue("profile_university");
            setSavedUniversity(savedValue);
        };

        loadSavedUniversity();
    }, []);

    useEffect(() => {
        const updateAuth = async () => {
            if (authUpdate && !saveStatus) {
                try {
                    removeErrorByKey(errorItems, "global");

                    const curcumstancesValue = (await Storage.getValue(storedDataKey)) ?? "";
                    const circumstancesObjectMap = curcumstancesValue.split(",").filter((val) => !!val) as CircumstancesType[];

                    if (circumstancesObjectMap.length === 0) {
                        setSaveStatus(false);
                        const newErrors = addErrorEntry(errorItems, {
                            key: "circumstances",
                            message: "Select at least one circumstance",
                            tab: "Profile"
                        });
                        setErrorItems(newErrors);
                        return;
                    }

                    console.log("Persisting to auth");

                    // Ensure we have a valid session before updating user attributes
                    const session = await getSession();
                    if (!session) throw new Error("Failed to retrieve valid session.");

                    const res = await updateUserAttributes({
                        given_name: data.given_name,
                        family_name: data.family_name,
                        gender: data.gender,
                        "custom:circumstances": circumstancesObjectMap.join(","),
                    });

                    if (res !== 'SUCCESS') {
                        console.log("Fail persisting auth");
                        setAuthUpdate(true);
                        const newErrors = addErrorEntry(errorItems, {
                            key: "global",
                            message: "Network error when updating identity",
                            tab: "Profile"
                        });
                        setErrorItems(newErrors);
                    } else {
                        console.log("Auth persisted");
                        setAuthUpdate(false);
                    }

                    const getProfileFormDataFromStorage = mapStoreDataToProfileFields(data, circumstancesObjectMap);
                    setProfileFormDataContext(getProfileFormDataFromStorage);
                } catch (exception) {
                    console.log(exception);
                    const newErrors = addErrorEntry(errorItems, {
                        key: "global",
                        message: `Network error: ${exception}`,
                        tab: "Share"
                    });
                    setErrorItems(newErrors);
                }
            }
        };

        updateAuth();
    }, [authUpdate, saveStatus]);

    const handleUpdate = async () => {
        removeErrorByKey(errorItems, "global")
        setSaveStatus(false)
        //setShowLoader(true);


        //setShowLoader(false);
        //setIsFormValueChange(false);
        //setErrorMessage("");
        //setAlertVisibility(true);
        setShowLoader(false);
        //setErrorMessage(error.message);
        //setAlertVisibility(true);
    };


    const handleGenericField = async (event: CustomTextInputChangeEvent) => {

        const keyStorage = event.name;

        setSaveStatus(false);
        await Storage.setValue(keyStorage, event.value);
        await Storage.setValue("form_changed", true);

    }


    const handleFirstname = async (event: CustomTextInputChangeEvent) => {

        const keyForm = "firstname";
        const keyStorage = "given_name";


        const updatedErrors = removeErrorByKey(errorItems, keyForm);

        if (
          !event.value || event.value.length == 0
        ) {
            // Add a new error for "dob"
            const newErrors = addErrorEntry(updatedErrors, {
                key: keyForm,
                message: "Invalid first name. Please enter a value.",
                tab: "Profile"
            });

            setErrorItems(newErrors); // Update the state with new errors
        } else {
            setAuthUpdate(true);
            await Storage.setValue(keyStorage, event.value);
            setErrorItems(updatedErrors); // Update the state to reflect error removal
        }
    };

    const handleLastname = async (event: CustomTextInputChangeEvent) => {

        const keyForm = "lastname";
        const keyStorage = "family_name";

        const updatedErrors = removeErrorByKey(errorItems, keyForm);

        if (
          !event.value || event.value.length == 0
        ) {
            // Add a new error for "dob"
            const newErrors = addErrorEntry(updatedErrors, {
                key: keyForm,
                message: "Invalid last name. Please enter a value.",
                tab: "Profile"
            });

            setErrorItems(newErrors); // Update the state with new errors
        } else {
            setAuthUpdate(true);
            console.log("auth:" + authUpdate);
            await Storage.setValue(keyStorage, event.value);
            setErrorItems(updatedErrors); // Update the state to reflect error removal
        }
    };

    const handleEmail = async (event: CustomTextInputChangeEvent) => {

        const keyForm = "email";
        const keyStorage = "email";

        const updatedErrors = removeErrorByKey(errorItems, keyForm);

        if (
          !event.value || event.value.length == 0 ||
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(event.value)
        ) {
            // Add a new error for "dob"
            const newErrors = addErrorEntry(updatedErrors, {
                key: keyForm,
                message: "Invalid email. Please enter a valid email.",
                tab: "Profile"
            });

            setErrorItems(newErrors); // Update the state with new errors
        } else {
            setAuthUpdate(true);
            await Storage.setValue(keyStorage, event.value);
            setErrorItems(updatedErrors); // Update the state to reflect error removal
        }
    };

    const handleDob = async (event: CustomTextInputChangeEvent) => {
        const keyForm = "dob";
        const keyStorage = "profile_dob";

        setSaveStatus(false);
        await Storage.setValue("form_changed", true);
        const updatedErrors = removeErrorByKey(errorItems, keyForm);

        // Validate for DD/MM/YYYY format
        if (
          event.value &&
          !/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/.test(event.value)
        ) {
            // Add a new error for "dob"
            const newErrors = addErrorEntry(updatedErrors, {
                key: keyForm,
                message: "Invalid date. Please enter the date in the format DD/MM/YYYY.",
                tab: "Profile"
            });

            setErrorItems(newErrors);
        } else {
            // Convert DD/MM/YYYY to YYYY-MM-DD before saving
            const [day, month, year] = event.value.split("/");
            const formattedDate = `${year}-${month}-${day}`;

            await Storage.setValue(keyStorage, formattedDate);
            setErrorItems(updatedErrors);
        }
    };

    const handleDeleteUser = useCallback(() => {
        navigatino.navigate(ProfileStackRoute, {
            screen: DeleteScreenRoute,
        });
    }, []);

    const selectedUniversity = university_data.find(
      (university) => university.value === profileFormData.profile_university
    );


    const genderOptions = useMemo(() => {
        return [
            "Female/woman",
            "Male/man",
            "Non-binary",
            "Transgender",
            "Intersex",
            "Other",
        ];
    }, []);

    useEffect(() => {
        const profileFormDataChanged = async () => {
            if (profileFormDataContext) {
                const isSync = await isDataSync();
                setIsFormValueChange(!isSync);
            }
        };

        profileFormDataChanged();
    }, [profileFormData, profileFormDataContext]);

    useEffect(() => {
        const updateProfileDataFromStorage = async () => {
            const _data = await Storage.getStorage();
            const curcumstancesValue =
              ((await Storage.getValue(storedDataKey)) as string) ?? "";
            const circumstancesObjectMap = curcumstancesValue
              .split(",")
              .filter((val) => !!val) as CircumstancesType[];

            const getProfileFormDataFromStorage = mapStoreDataToProfileFields(
              _data,
              circumstancesObjectMap
            );

            setProfileFormData((prev) => ({
                ...prev,
                ...getProfileFormDataFromStorage,
            }));

            setProfileFormDataContext(getProfileFormDataFromStorage);
        };

        updateProfileDataFromStorage();
    }, []);

    const isDataSync = async () => {
        const _data = await Storage.getStorage();
        const curcumstancesValue =
          ((await Storage.getValue(storedDataKey)) as string) ?? "";
        const circumstancesObjectMap = curcumstancesValue
          .split(",")
          .filter((val) => !!val) as CircumstancesType[];

        const profileFormDataFromStorage: IProfileForm =
          mapStoreDataToProfileFields(_data, circumstancesObjectMap);

        return isProfileStorageAndContextDataSame(profileFormDataFromStorage);
    };

    const isProfileStorageAndContextDataSame = (data: IProfileForm) => {
        const keys = Object.keys(data);

        return keys.every((key) => {
            const storageValue = data[key as keyof IProfileForm];
            const contextValue = profileFormDataContext[key as keyof IProfileForm];

            if (Array.isArray(storageValue) && Array.isArray(contextValue)) {
                return arraysAreEqual(storageValue, contextValue);
            } else {
                return storageValue === contextValue;
            }
        });
    };

    const onChangeTextField = async (event: CustomTextInputChangeEvent) => {
        const {name, value} = event;
        setSaveStatus(false);
        await Storage.setValue(name, value);
        await Storage.setValue("form_changed", true);
        setProfileFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const onPressCircumstancesCheckboxes = async (event: CheckboxPressEvent) => {
        const {name, checked} = event;
        setSaveStatus(false);
        let updatedCircumstances;
        setProfileFormData((prevState) => {
            updatedCircumstances = checked
              ? [...prevState.circumstances, name as CircumstancesType]
              : prevState.circumstances.filter((n) => n !== name);

            return {
                ...prevState,
                circumstances: updatedCircumstances,
            };
        });
        if (updatedCircumstances) {
            await setMode(updatedCircumstances);
            const isSync = await isDataSync();
            setIsFormValueChange(!isSync);
        }
    };

    const renderCircumstancesCheckboxes = () => {
        return (
          <View>
              <Text style={{fontSize: 18, fontWeight: "500"}}>Circumstances:</Text>
              <Checkbox
                name={CircumstancesType.NEURODIVERSITY}
                text={CircumstancesType.NEURODIVERSITY}
                checked={profileFormData.circumstances.includes(
                  CircumstancesType.NEURODIVERSITY
                )}
                onPressCheckbox={onPressCircumstancesCheckboxes}
              />
              <Checkbox
                name={CircumstancesType.MENTAL_HEALTH}
                text={CircumstancesType.MENTAL_HEALTH}
                checked={profileFormData.circumstances.includes(
                  CircumstancesType.MENTAL_HEALTH
                )}
                onPressCheckbox={onPressCircumstancesCheckboxes}
              />
              <Checkbox
                name={CircumstancesType.WELLBEING}
                text={CircumstancesType.WELLBEING}
                checked={profileFormData.circumstances.includes(
                  CircumstancesType.WELLBEING
                )}
                onPressCheckbox={onPressCircumstancesCheckboxes}
              />
              <Checkbox
                name={CircumstancesType.DISABILITY}
                text={CircumstancesType.DISABILITY}
                checked={profileFormData.circumstances.includes(
                  CircumstancesType.DISABILITY
                )}
                onPressCheckbox={onPressCircumstancesCheckboxes}
              />
              {getErrorMessage(errorItems, "circumstances") ?
                <Text style={styles.errorText}>{getErrorMessage(errorItems, "circumstances")}</Text> : null}
          </View>

        );
    };


    return (
      <>
          {showLoader ? (
            <PageContainer loading></PageContainer>
          ) : (
            <KeyboardAvoidingView
              style={[
                  {flex: 1, backgroundColor: theme.BACKGROUND},
                  !IsMobile && {width: WEB_MAXWIDTH, alignSelf: "center"},
              ]}
              behavior={Platform.select({ios: "padding", android: "height"})}
            >
                <ScrollView
                  style={styles.container}
                  scrollEventThrottle={1600}
                  showsVerticalScrollIndicator={false}
                >
                    <View style={{position: "relative", width: "100%", height: 95}}>
                        <CurvedBgImage/>
                        <View style={styles.header}>
                            <Text style={styles.heading}>Please fill out your details</Text>
                        </View>
                    </View>
                    <View style={{
                        paddingVertical: 10, gap: 25, paddingRight: 20,
                        paddingLeft: 20,
                        paddingBottom: 20,
                    }}>
                        <CustomTextInput
                          name="given_name"
                          title="First name"
                          defaultValue={profileFormData.given_name}
                          onChangeText={handleFirstname}
                          fieldStyles={styles.input}
                          required
                        />
                        {getErrorMessage(errorItems, "firstname") ?
                          <Text style={styles.errorText}>{getErrorMessage(errorItems, "firstname")}</Text> : null}
                        <CustomTextInput
                          name="family_name"
                          title="Last Name"
                          defaultValue={profileFormData.family_name}
                          onChangeText={handleLastname}
                          fieldStyles={styles.input}
                          required
                        />
                        {getErrorMessage(errorItems, "lastname") ?
                          <Text style={styles.errorText}>{getErrorMessage(errorItems, "lastname")}</Text> : null}
                        <CustomTextInput
                          name="email"
                          title="Email"
                          defaultValue={profileFormData.email}
                          onChangeText={handleEmail}
                          fieldStyles={styles.input}
                          required
                        />
                        {getErrorMessage(errorItems, "email") ?
                          <Text style={styles.errorText}>{getErrorMessage(errorItems, "email")}</Text> : null}
                        <CustomTextInput
                          name="profile_nickname"
                          title="Nickname"
                          defaultValue={profileFormData.profile_nickname}
                          onChangeText={handleGenericField}
                          fieldStyles={styles.input}
                          required
                        />
                        {getErrorMessage(errorItems, "nickname") ?
                          <Text style={styles.errorText}>{getErrorMessage(errorItems, "nickname")}</Text> : null}
                        <CustomTextInput
                          name="profile_dob"
                          title="Date of Birth"
                          defaultValue={profileFormData.profile_dob
                          && profileFormData.profile_dob != null
                          && profileFormData.profile_dob != "null" ? profileFormData.profile_dob.split("-").reverse().join("/") : ""}
                          onChangeText={handleDob}
                          fieldStyles={styles.input}
                          required
                        />
                        {getErrorMessage(errorItems, "dob") ?
                          <Text style={styles.errorText}>{getErrorMessage(errorItems, "dob")}</Text> : null}

                        <CustomTextInput
                          name="profile_gp_details"
                          title="GP Surgery & number"
                          defaultValue={profileFormData.profile_gp_details}
                          onChangeText={handleGenericField}
                          fieldStyles={styles.input}
                          required
                        />

                        <CustomTextInput
                          name="profile_support_network"
                          title="Supporting friends and family (Please insert name(s))"
                          defaultValue={profileFormData.profile_support_network}
                          onChangeText={handleGenericField}
                          fieldStyles={styles.input}
                          required
                        />
                        <CustomTextInput
                          name="profile_support_services"
                          title="Supporting Services: (e.g. mentoring/coaching, counselling service, CPN,
            Psychiatrist)"
                          defaultValue={profileFormData.profile_support_services}
                          onChangeText={handleGenericField}
                          fieldStyles={styles.input}
                          required
                        />
                        <CustomTextInput
                          name="profile_contacts"
                          title="Trusted Contact Name & Number (Someone who I would contact if my mental health
              worsens or I need more support)"
                          defaultValue={profileFormData.profile_contacts}
                          onChangeText={handleGenericField}
                          fieldStyles={styles.input}
                          required
                        />
                        <CustomTextInput
                          name="profile_work"
                          title="Who supports me at University: (Please insert name(s))"
                          defaultValue={profileFormData.profile_work}
                          onChangeText={handleGenericField}
                          fieldStyles={styles.input}
                          required
                        />
                        <View style={{paddingHorizontal: 10, gap: 15}}>
                            <Dropdown
                              defaultValue={profileFormData.gender}
                              onSelect={async (value) => {
                                  setAuthUpdate(true);
                                  await Storage.setValue("gender", value);
                                  setProfileFormData((prevState) => {
                                      return {
                                          ...prevState,
                                          gender: value,
                                      };
                                  });
                                  const isSync = await isDataSync();
                                  setIsFormValueChange(!isSync);
                              }}
                              label="Gender"
                              simple
                              data={genderOptions}
                            />
                        </View>
                        {renderCircumstancesCheckboxes()}
                        <View style={{paddingHorizontal: 10, gap: 15}}>
                            <UniversityDropdown
                              onSelect={async (selectedItem, index) => {
                                  setSaveStatus(false);
                                  setAuthUpdate(true);
                                  // Use selectedItem.value directly
                                  console.log('save ' + selectedItem.value);
                                  await Storage.setValue("profile_university", selectedItem.value);
                                  setProfileFormData((prevState) => {
                                      return {
                                          ...prevState,
                                          profile_university: selectedItem.value,
                                      };
                                  });
                                  setSaveStatus(false);
                                  const isSync = await isDataSync();
                                  setIsFormValueChange(!isSync);
                              }}
                              selectedUniversity={savedUniversity}
                            />
                        </View>
                        <CustomTextInput
                          name="profile_course"
                          title="Course"
                          defaultValue={profileFormData.profile_course}
                          onChangeText={handleGenericField}
                          fieldStyles={styles.input}
                          required
                        />
                        <View
                          style={{gap: 20, paddingVertical: 5, alignItems: "center"}}
                        >
                            {getErrorMessage(errorItems, "global") ?
                              <Text style={styles.errorText}>{getErrorMessage(errorItems, "global")}</Text> : null}
                            <SaveButton onSave={handleUpdate} disable={saveStatus && !authUpdate}/>
                            <Button
                              variant={ButtonType.secondary}
                              bgColor={theme.ERROR}
                              title="Delete Account"
                              onPress={handleDeleteUser}
                            />
                        </View>
                        <Text style={{color: "gray", fontSize: 14, textAlign: "center", marginVertical: 20}}>
                            Version {appVersion}
                        </Text>
                    </View>
                </ScrollView>
            </KeyboardAvoidingView>
          )}
      </>
    );
};

export default ProfileScreen;

const getStyles = (theme: AppTheme) =>
  StyleSheet.create({
      container: {
          flex: 1,
      },
      header: {
          height: 85,
          paddingRight: 20,
          paddingLeft: 20,
          justifyContent: "center",
          alignItems: "center",
          borderBottomStartRadius: 50,
          borderBottomEndRadius: 50,
          width: "100%",
          backgroundColor: "transparent", // Changed from theme.BACKGROUND
          zIndex: 2, // Keep the text on top of the image
      },
      heading: {
          fontSize: 20,
          fontWeight: "600",
          color: theme.TEXT,
          textAlign: "center",
      },
      input: {
          backgroundColor: theme.BACKGROUND,
          borderBottomColor: "#ddd",
          borderBottomWidth: 1,
          paddingVertical: 5,
          paddingHorizontal: 0,
      },
      errorText: {
          color: "red",
          marginTop: 5,
      }
  });
